import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { VisuallyHidden } from '../../internals/VisuallyHidden';
import { rem } from '../../tokens/spacing';
export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: SpinnerSize;
  borderColor?: string;
  color?: string;
  semanticId?: string;
}

const rotation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const Root = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['$size', '$borderColor'].includes(String(prop)),
})<{
  $size: SpinnerSize;
  $borderColor: string;
}>`
  pointer-events: none;
  border: 3px solid var(--ui-spinner-track, var(--ui-border));
  border-radius: 50%;
  display: inline-block;
  animation: ${rotation} 1.5s linear infinite;

  border-bottom-color: ${({ $borderColor }) => $borderColor};

  ${({ $size }) => {
    if ($size === 'sm') {
      return css`
        width: ${rem(16)};
        height: ${rem(16)};
      `;
    }
    if ($size === 'md') {
      return css`
        width: ${rem(40)};
        height: ${rem(40)};
      `;
    }
    if ($size === 'lg') {
      return css`
        width: ${rem(52)};
        height: ${rem(52)};
      `;
    }
  }};
`;

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(function Spinner(
  { size = 'lg', borderColor, color, semanticId, role = 'status', ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const resolvedColor = borderColor ?? color ?? 'var(--ui-spinner-border, var(--ui-interactive))';

  return (
    <Root
      ref={ref}
      $size={size}
      $borderColor={resolvedColor}
      role={role}
      aria-label="Loading"
      {...semanticProps}
      {...rest}
    >
      <VisuallyHidden>Loading...</VisuallyHidden>
    </Root>
  );
});

Spinner.displayName = 'Spinner';
